import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Receipt as ReceiptIcon, CreditCard, HandCoins, Warning, GearSix, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api, errMsg, useApi, inr, fmtDate, loadRazorpay } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { PageHeader, GlassCard, Modal, Field, Loader, Empty, Table, Badge, ExportButtons, StatCard, fadeUp } from "../components/kit";
import { FeeEntry } from "../components/fees/FeeEntry";
import { FeeGenerate, FeeReports } from "../components/fees/FeeGenerate";

const tabCls = "rounded-full px-4 py-1.5 data-[state=active]:bg-blue-500 data-[state=active]:text-white";

function CollectForm({ inv, onClose, onSaved }) {
  const { t } = useT();
  const [f, setF] = useState({ amount: inv.balance, method: "cash", note: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/fees/invoices/${inv.id}/collect`, { ...f, amount: Number(f.amount) });
      toast.success(`Collected · ${r.data.receipt_no}`);
      onSaved(r.data);
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`${t("Collect")} — ${inv.student_name}`} description={`${inv.title} · balance ${inr(inv.balance)}`} testid="collect-dialog">
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("Amount")}><input type="number" min={1} max={inv.balance} step="0.01" className="field" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="collect-amount-input" /></Field>
        <Field label="Method">
          <select className="field" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="collect-method-select">
            {[["cash", "Cash"], ["upi", "UPI"], ["cheque", "Cheque"], ["bank_transfer", "Bank transfer"], ["card", "Card (POS)"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Note"><input className="field" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="collect-note-input" /></Field>
        <button className="btn-primary w-full" data-testid="collect-submit-btn">{t("Collect")}</button>
      </form>
    </Modal>
  );
}

function EditInvoice({ inv, onClose, onSaved }) {
  const [f, setF] = useState({ amount: inv.amount, due_date: inv.due_date || "", remarks: inv.remarks || "" });
  const send = async (body, msg) => {
    try {
      await api.put(`/fees/invoices/${inv.id}`, body);
      toast.success(msg);
      onSaved();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Edit — ${inv.title}`} description={`${inv.student_name} · paid ${inr(inv.paid_amount)}`} testid="edit-invoice-dialog">
      <form onSubmit={(e) => { e.preventDefault(); send({ ...f, amount: Number(f.amount) }, "Fee updated"); }} className="space-y-4">
        <Field label="Amount (₹)"><input type="number" min={Math.max(1, inv.paid_amount)} step="0.01" className="field" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="edit-invoice-amount" /></Field>
        <Field label="Due date"><input type="date" className="field" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="edit-invoice-due" /></Field>
        <Field label="Remarks"><input className="field" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="edit-invoice-remarks" /></Field>
        <button className="btn-primary w-full" data-testid="edit-invoice-save">Save</button>
        {inv.paid_amount === 0 && <button type="button" className="btn-ghost w-full !text-rose-300" onClick={() => window.confirm("Cancel this fee? It stays in records as cancelled.") && send({ cancel: true }, "Fee cancelled")} data-testid="edit-invoice-cancel">Cancel this fee</button>}
      </form>
    </Modal>
  );
}

function AdminFees({ t }) {
  const { data: classes } = useApi("/classes");
  const { data: school } = useApi("/school");
  const { data: tdata } = useApi("/fees/types");
  const [status, setStatus] = useState("unpaid");
  const [cls, setCls] = useState("");
  const [year, setYear] = useState("");
  const [ftype, setFtype] = useState("");
  const inv = useApi(`/fees/invoices?status=${status}&class_id=${cls}&academic_year=${encodeURIComponent(year)}&fee_type_id=${ftype}`);
  const pays = useApi("/fees/payments");
  const [collect, setCollect] = useState(null);
  const [edit, setEdit] = useState(null);
  const reloadAll = () => { inv.reload(); pays.reload(); };
  const ay = school?.academic_year || "";
  const years = ay ? [`${+ay.slice(0, 4) - 1}-${ay.slice(2, 4)}`, ay, `${+ay.slice(0, 4) + 1}-${String(+ay.slice(0, 4) + 2).slice(2)}`] : [];
  const totalDue = (inv.data || []).reduce((a, i) => a + i.balance, 0);
  return (
    <div>
      <PageHeader eyebrow="Finance" title={t("Fees")} subtitle="Class-wise fee structures, fee entry, collection and reports."
        actions={<><ExportButtons entity="invoices" /><Link to="/fee-settings" className="btn-primary" data-testid="fee-settings-link"><GearSix size={16} /> Fee settings</Link></>} />
      <Tabs defaultValue="invoices">
        <TabsList className="mb-6 bg-white/5 border border-white/10 rounded-full p-1 h-auto flex-wrap">
          <TabsTrigger value="invoices" className={tabCls} data-testid="tab-invoices">{t("Invoices")}</TabsTrigger>
          <TabsTrigger value="entry" className={tabCls} data-testid="tab-fee-entry">Fee entry</TabsTrigger>
          <TabsTrigger value="generate" className={tabCls} data-testid="tab-generate">Generate for class</TabsTrigger>
          <TabsTrigger value="reports" className={tabCls} data-testid="tab-fee-reports">Reports</TabsTrigger>
          <TabsTrigger value="payments" className={tabCls} data-testid="tab-payments">{t("Payments")}</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices">
          <GlassCard className="p-4 sm:p-6">
            <div className="mb-4 flex flex-col lg:flex-row gap-3 lg:items-center">
              <select className="field lg:!w-36" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="invoice-status-filter">
                <option value="unpaid">Unpaid</option><option value="paid">{t("Paid")}</option><option value="">All</option>
              </select>
              <select className="field lg:!w-36" value={year} onChange={(e) => setYear(e.target.value)} data-testid="invoice-year-filter">
                <option value="">All sessions</option>{years.map((y) => <option key={y}>{y}</option>)}
              </select>
              <select className="field lg:!w-44" value={cls} onChange={(e) => setCls(e.target.value)} data-testid="invoice-class-filter">
                <option value="">{t("All classes")}</option>
                {classes?.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}
              </select>
              <select className="field lg:!w-52" value={ftype} onChange={(e) => setFtype(e.target.value)} data-testid="invoice-type-filter">
                <option value="">All fee types</option>{tdata?.types.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <div className="lg:ml-auto text-sm text-zinc-400">Balance: <span className="font-display text-lg text-orange-300" data-testid="invoice-balance-total">{inr(totalDue)}</span></div>
            </div>
            {inv.loading ? <Loader /> : !inv.data?.length ? <Empty /> : (
              <Table testid="invoices-table">
                <thead><tr><th>Student</th><th>{t("Class")}</th><th>Fee</th><th>Session</th><th>{t("Amount")}</th><th>{t("Paid")}</th><th>Balance</th><th>{t("Due date")}</th><th>{t("Status")}</th><th></th></tr></thead>
                <tbody>
                  {inv.data.map((i) => (
                    <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
                      <td className="font-semibold text-white">{i.student_name}</td><td>{i.class_name}</td><td>{i.title}{i.remarks ? <span className="block text-[11px] text-zinc-500">{i.remarks}</span> : null}</td><td className="text-zinc-400">{i.academic_year}</td><td>{inr(i.amount)}</td><td>{inr(i.paid_amount)}</td>
                      <td className="text-orange-300">{inr(i.balance)}</td><td>{fmtDate(i.due_date)}</td><td><Badge status={i.status}>{t(i.status[0].toUpperCase() + i.status.slice(1))}</Badge></td>
                      <td className="text-right whitespace-nowrap">{i.status !== "cancelled" && <button className="btn-ghost !p-2 mr-1" onClick={() => setEdit(i)} data-testid={`edit-invoice-btn-${i.id}`}><PencilSimple size={15} /></button>}
                        {i.balance > 0 && i.status !== "cancelled" && <button className="btn-ghost !py-1.5 !px-3" onClick={() => setCollect(i)} data-testid={`collect-btn-${i.id}`}><HandCoins size={15} /> {t("Collect")}</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </GlassCard>
        </TabsContent>
        <TabsContent value="entry"><FeeEntry /></TabsContent>
        <TabsContent value="generate">{ay ? <FeeGenerate year={ay} /> : <Loader />}</TabsContent>
        <TabsContent value="reports">{ay ? <FeeReports year={ay} years={years} classes={classes} /> : <Loader />}</TabsContent>
        <TabsContent value="payments">
          <GlassCard className="p-4 sm:p-6">
            <div className="mb-4 flex justify-end gap-2"><ExportButtons entity="payments" /></div>
            {pays.loading ? <Loader /> : !pays.data?.length ? <Empty /> : (
              <Table testid="payments-table">
                <thead><tr><th>{t("Receipt")}</th><th>Student</th><th>{t("Title")}</th><th>{t("Amount")}</th><th>Method</th><th>{t("Date")}</th><th></th></tr></thead>
                <tbody>
                  {pays.data.map((p) => (
                    <tr key={p.id}>
                      <td className="font-mono text-xs">{p.receipt_no}</td><td className="font-semibold text-white">{p.student_name}</td><td>{p.title}</td>
                      <td className="text-emerald-300">{inr(p.amount)}</td><td className="capitalize">{p.method.replace("_", " ")}</td><td>{fmtDate(p.paid_at)}</td>
                      <td className="text-right"><Link to={`/receipt/${p.id}`} className="btn-ghost !py-1.5 !px-3" data-testid={`receipt-link-${p.id}`}><ReceiptIcon size={15} /> {t("Receipt")}</Link></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </GlassCard>
        </TabsContent>
      </Tabs>
      {edit && <EditInvoice inv={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reloadAll(); }} />}
      {collect && <CollectForm inv={collect} onClose={() => setCollect(null)} onSaved={() => { setCollect(null); reloadAll(); }} />}
    </div>
  );
}

function ParentFees({ t }) {
  const { user } = useAuth();
  const inv = useApi("/fees/invoices");
  const pays = useApi("/fees/payments");
  const { data: school } = useApi("/school");
  const [busy, setBusy] = useState("");
  const pay = async (i) => {
    setBusy(i.id);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load the payment window. Check your connection.");
      const { data: o } = await api.post(`/fees/invoices/${i.id}/order`);
      const rzp = new window.Razorpay({
        key: o.key_id, order_id: o.order_id, amount: o.amount, currency: o.currency, name: o.school_name,
        description: `${o.title} — ${o.student_name}`, prefill: { name: user.name, email: user.email, contact: user.phone },
        theme: { color: "#3B82F6" },
        handler: async (res) => {
          try {
            await api.post("/fees/verify", res);
            toast.success("Payment successful");
            inv.reload();
            pays.reload();
          } catch (e) {
            toast.error(errMsg(e));
          }
        },
        modal: { ondismiss: () => setBusy("") },
      });
      rzp.on("payment.failed", (r) => toast.error(r.error?.description || "Payment failed"));
      rzp.open();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const unpaid = (inv.data || []).filter((i) => i.balance > 0);
  return (
    <div>
      <PageHeader eyebrow="Payments" title={t("Fees")} subtitle="Pay securely online — money goes directly to your school's account." />
      {school && !school.payments_connected && (
        <div className="mb-6 flex items-center gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-5 py-3 text-sm text-amber-200" data-testid="payments-not-configured">
          <Warning size={18} /> Online payment isn't enabled by your school yet. You can pay at the school office.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard icon={CreditCard} label={t("Fee due")} value={inr(unpaid.reduce((a, i) => a + i.balance, 0))} tone="orange" testid="parent-fee-due" />
        <StatCard icon={ReceiptIcon} label={t("Paid")} value={inr((pays.data || []).reduce((a, p) => a + p.amount, 0))} tone="green" testid="parent-fee-paid" delay={0.05} />
        <StatCard icon={HandCoins} label={t("Pending")} value={unpaid.length} tone="blue" testid="parent-fee-count" delay={0.1} />
      </div>
      {inv.loading ? <Loader /> : Object.entries((inv.data || []).reduce((g, i) => ({ ...g, [i.fee_type || "Other"]: [...(g[i.fee_type || "Other"] || []), i] }), {})).map(([type, list]) => (
        <section key={type} className="mb-8" data-testid={`parent-fee-group-${type}`}>
          <div className="mb-3 flex items-baseline justify-between"><h3 className="font-display text-lg text-white">{type}</h3>
            <span className="text-xs text-zinc-400">{inr(list.reduce((a, i) => a + i.paid_amount, 0))} paid of {inr(list.reduce((a, i) => a + i.amount, 0))}{list.some((i) => i.balance > 0) && <span className="ml-2 text-orange-300">{inr(list.reduce((a, i) => a + i.balance, 0))} due</span>}</span></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {list.map((i, k) => (
            <motion.div key={i.id} {...fadeUp(k)}>
              <GlassCard hover className="p-6 flex items-center justify-between gap-4" data-testid={`parent-invoice-${i.id}`}>
                <div className="min-w-0">
                  <div className="eyebrow">{i.student_name} · {i.class_name}{i.academic_year ? ` · ${i.academic_year}` : ""}</div>
                  <div className="mt-2 font-display text-xl text-white">{i.title}</div>
                  <div className="text-xs text-zinc-400">{t("Due")} {fmtDate(i.due_date)} · <Badge status={i.status}>{t(i.status[0].toUpperCase() + i.status.slice(1))}</Badge></div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-2xl text-white">{inr(i.balance > 0 ? i.balance : i.amount)}</div>
                  {i.balance > 0 && <button className="btn-accent mt-3" disabled={busy === i.id || !school?.payments_connected} onClick={() => pay(i)} data-testid={`pay-btn-${i.id}`}>{busy === i.id ? "…" : t("Pay now")}</button>}
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
        </section>
      ))}
      <h2 className="mt-12 mb-4 font-display text-base md:text-lg text-white">{t("Payments")}</h2>
      <GlassCard className="p-4 sm:p-6">
        {!pays.data?.length ? <Empty /> : (
          <Table testid="parent-payments-table">
            <thead><tr><th>{t("Receipt")}</th><th>{t("Title")}</th><th>{t("Amount")}</th><th>{t("Date")}</th><th></th></tr></thead>
            <tbody>
              {pays.data.map((p) => (
                <tr key={p.id}><td className="font-mono text-xs">{p.receipt_no}</td><td>{p.title} · {p.student_name}</td><td className="text-emerald-300">{inr(p.amount)}</td><td>{fmtDate(p.paid_at)}</td>
                  <td className="text-right"><Link to={`/receipt/${p.id}`} className="btn-ghost !py-1.5 !px-3" data-testid={`receipt-link-${p.id}`}><ReceiptIcon size={15} /> {t("Receipt")}</Link></td></tr>
              ))}
            </tbody>
          </Table>
        )}
      </GlassCard>
    </div>
  );
}

export default function Fees() {
  const { user } = useAuth();
  const { t } = useT();
  return user.role === "admin" ? <AdminFees t={t} /> : <ParentFees t={t} />;
}
